import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileDown, Loader2, CheckCircle2, AlertTriangle,
  ShieldAlert, HelpCircle, Plus, Ban, Stethoscope,
} from 'lucide-react';
import { visiteAlignApi } from '../lib/api';
import { toast } from 'sonner';

const MATCH_STYLE = {
  CERTO:       { label: 'Certo',       bg: 'bg-emerald-100', color: 'text-emerald-800', icon: CheckCircle2 },
  PROBABILE:   { label: 'Probabile',   bg: 'bg-amber-100',   color: 'text-amber-900',   icon: AlertTriangle },
  AMBIGUO:     { label: 'Ambiguo',     bg: 'bg-orange-100',  color: 'text-orange-900',  icon: ShieldAlert },
  NON_TROVATO: { label: 'Non trovato', bg: 'bg-slate-100',   color: 'text-slate-700',   icon: HelpCircle },
};

const ACTION_STYLE = {
  NEW:             { label: 'NEW',       bg: 'bg-sky-100',   color: 'text-sky-800',   icon: Plus },
  GIA_PRESENTE:    { label: 'GIÀ',       bg: 'bg-slate-100', color: 'text-slate-700', icon: CheckCircle2 },
  NON_APPLICABILE: { label: 'N/A',       bg: 'bg-red-50',    color: 'text-red-700',   icon: Ban },
  ERRORE_DATI:     { label: 'ERR',       bg: 'bg-red-100',   color: 'text-red-800',   icon: Ban },
};

const Pill = ({ style, text }) => {
  const Ic = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${style.bg} ${style.color}`}>
      <Ic size={10} /> {text || style.label}
    </span>
  );
};

const VisiteAlignImportPage = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [filterMatch, setFilterMatch] = useState('ALL');
  const [filterAction, setFilterAction] = useState('ALL');
  const [applying, setApplying] = useState(false);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
  };

  const uploadPreview = async () => {
    if (!file) { toast.error('Seleziona un file Excel'); return; }
    setUploading(true);
    setPreview(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await visiteAlignApi.previewImport(fd);
      setPreview(data);
      const c = data.counters || {};
      toast.success(
        `${data.total_rows} righe · NEW ${c.NEW || 0} · GIÀ ${c.GIA_PRESENTE || 0} · AMB ${c.AMBIGUO || 0} · N/T ${c.NON_TROVATO || 0}`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore preview');
    } finally {
      setUploading(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    return (preview.rows || []).filter((r) => {
      if (filterMatch !== 'ALL' && r.match_state !== filterMatch) return false;
      if (filterAction !== 'ALL' && r.action_state !== filterAction) return false;
      return true;
    });
  }, [preview, filterMatch, filterAction]);

  const doApply = async () => {
    if (!preview) return;
    const c = preview.counters || {};
    if (!window.confirm(
      `Applicare le modifiche al DB?\n\n` +
      `· NEW (nuovi inserimenti): ${c.NEW || 0}\n\n` +
      `SALTATE: ${c.AMBIGUO || 0} AMBIGUO + ${c.NON_TROVATO || 0} NON_TROVATO + ${c.GIA_PRESENTE || 0} già presenti.\n\n` +
      `Backup automatico via $out prima delle scritture.`,
    )) return;
    setApplying(true);
    try {
      const { data } = await visiteAlignApi.applyImport({ token: preview.token });
      toast.success(`Inserite: ${data.inserted} visite · Backup: ${data.backup_collection}`);
      setPreview(null);
      setFile(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore apply');
    } finally {
      setApplying(false);
    }
  };

  const c = preview?.counters || {};

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="visite-align-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900" data-testid="va-back">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#e0e7ff] text-[#023F80] flex items-center justify-center">
              <Stethoscope size={18} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#023F80]">Import visite mediche via Excel</h1>
              <p className="text-sm text-slate-500">
                Match CF → fallback Nome+Cognome · idempotency <code>(workerId + data visita + tipo)</code> · backup automatico prima delle scritture.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => visiteAlignApi.downloadTemplate()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-200 hover:bg-slate-50"
          data-testid="va-download-template"
        >
          <FileDown size={14} /> Scarica template
        </button>
      </div>

      {/* Uploader */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <p className="text-sm font-semibold text-slate-700 mb-1">1. Carica il file Excel</p>
        <p className="text-xs text-slate-500 mb-3">
              Colonne riconosciute (case-insensitive, alias automatici): <code>Azienda</code>, <code>Lavoratore</code> o <code>Cognome</code>+<code>Nome</code>, <code>CodiceFiscale</code>, <code>Mansione</code>, <code>Tipo</code>, <code>Periodicità</code>, <code>Esito</code>, <code>DataVisita</code> <b>(obbl.)</b>, <code>Scadenza</code> (o "Da sottoporre a nuova visita entro"), <code>Medico Competente</code>, <code>Note</code>.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".xlsx,.xlsm,.xlsb"
            onChange={onFileChange}
            data-testid="va-file-input"
            className="text-xs file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          />
          <button
            onClick={uploadPreview}
            disabled={!file || uploading}
            data-testid="va-preview-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Analizzo…' : 'Anteprima'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-4">
          <p className="text-sm font-semibold text-slate-700">
            2. Anteprima · <span className="text-slate-500 font-normal">{preview.filename} · {preview.total_rows} righe</span>
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {[
              ['CERTO (CF)', c.CERTO, 'emerald'],
              ['PROBABILE (nome)', c.PROBABILE, 'amber'],
              ['AMBIGUO', c.AMBIGUO, 'orange'],
              ['NON TROVATO', c.NON_TROVATO, 'slate'],
              ['NEW', c.NEW, 'sky'],
              ['GIÀ PRESENTE', c.GIA_PRESENTE, 'slate'],
              ['NON APPLICABILE', c.NON_APPLICABILE, 'red'],
              ['ERRORE DATI', c.ERRORE_DATI, 'red'],
            ].map(([lbl, val, col]) => (
              <div key={lbl} className={`rounded-lg px-2 py-1.5 bg-${col}-50 border border-${col}-100`}>
                <div className="text-[9px] uppercase tracking-widest font-semibold text-slate-500">{lbl}</div>
                <div className={`text-lg font-bold text-${col}-700`}>{val || 0}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Filtri:</span>
            {['ALL', 'CERTO', 'PROBABILE', 'AMBIGUO', 'NON_TROVATO'].map((k) => (
              <button
                key={k}
                onClick={() => setFilterMatch(k)}
                data-testid={`va-filter-match-${k}`}
                className={`px-2 py-1 rounded-full text-[10px] font-bold ${filterMatch === k ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {k}
              </button>
            ))}
            <span className="text-slate-300 mx-1">|</span>
            {['ALL', 'NEW', 'GIA_PRESENTE', 'ERRORE_DATI', 'NON_APPLICABILE'].map((k) => (
              <button
                key={k}
                onClick={() => setFilterAction(k)}
                data-testid={`va-filter-action-${k}`}
                className={`px-2 py-1 rounded-full text-[10px] font-bold ${filterAction === k ? 'bg-[#023F80] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              >
                {k}
              </button>
            ))}
            <div className="ml-auto text-xs text-slate-500">{filteredRows.length} righe visibili</div>
          </div>

          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left bg-slate-50 px-2 py-2">#</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Match</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Azione</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Lavoratore Excel</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Lavoratore trovato</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Data</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Scadenza</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Tipo</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Esito</th>
                  <th className="text-left bg-slate-50 px-2 py-2">Errori</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.slice(0, 200).map((r) => (
                  <tr key={r.row_number} className="border-b border-slate-100" data-testid={`va-row-${r.row_number}`}>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-400">{r.row_number}</td>
                    <td className="px-2 py-1.5"><Pill style={MATCH_STYLE[r.match_state] || MATCH_STYLE.NON_TROVATO} /></td>
                    <td className="px-2 py-1.5"><Pill style={ACTION_STYLE[r.action_state] || ACTION_STYLE.NON_APPLICABILE} /></td>
                    <td className="px-2 py-1.5">
                      <div className="font-semibold">{r.input.lastName} {r.input.firstName}</div>
                      <div className="text-[9px] font-mono text-slate-400">{r.input.cf || '—'} · {r.input.azienda || '—'}</div>
                    </td>
                    <td className="px-2 py-1.5">
                      {r.worker ? (
                        <span className="text-emerald-800">{r.worker.lastName} {r.worker.firstName}</span>
                      ) : r.candidates?.length ? (
                        <span className="text-orange-700">{r.candidates.length} candidati</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5">{r.input.dataVisita || '—'}</td>
                    <td className="px-2 py-1.5">{r.input.scadenza || '—'}</td>
                    <td className="px-2 py-1.5 text-[10px]">{r.input.tipo || '—'}</td>
                    <td className="px-2 py-1.5 text-[10px]">{r.input.esito || '—'}</td>
                    <td className="px-2 py-1.5 text-[10px] text-red-700">{(r.errors || []).join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRows.length > 200 && (
              <div className="py-2 text-center text-[10px] text-slate-400">
                Mostrate prime 200 di {filteredRows.length}. Applica per processare tutte.
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              onClick={doApply}
              disabled={applying || !c.NEW}
              data-testid="va-apply-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white disabled:opacity-60"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {applying ? 'Applico…' : `Applica ${c.NEW || 0} nuove visite`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisiteAlignImportPage;
